import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, School, ChefHat, Building2, Clock, DollarSign, Shield, Sparkles, CheckCircle2, Zap, Globe, TrendingUp, Award, Lock, Database, Smartphone, BarChart3, Bell, CreditCard, FileText, UserCheck, MessageSquare, Gamepad2, Share2, FileImage, UserPlus, Settings, AlertCircle, Target, Layers, GitBranch, Code, Image, Send, ArrowDown, ArrowUp, Check, X, Heart, Languages, Landmark } from 'lucide-react';
import { ParentsTab, SchoolsTab, CaterersTab, DirectionsTab } from '../components/ActorsTabs';
import { useNavigate } from 'react-router-dom';
import '../styles/index.css';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeActorTab, setActiveActorTab] = useState('parents');
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <img src="/logo-ecolunch.png" alt="EcoLunch" className="logo-image" />
          </div>
          <div className="nav-links">
            <a href="#architecture" className="nav-link">Architecture</a>
            <a href="#acteurs" className="nav-link">Acteurs</a>
            <a href="#securite" className="nav-link">Sécurité</a>
            <div className="lang-selector">
              <button className="lang-btn active">FR</button>
              <button onClick={() => navigate('/en')} className="lang-btn">EN</button>
            </div>
            <button onClick={scrollToContact} className="nav-cta-btn">Demander un contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Sparkles size={16} />
            <span>Plateforme de Gestion Unifiée - Innovation Québécoise</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            EcoLunch : <span className="gradient-text">Plateforme de Gestion Automatisée</span> pour Repas Scolaires
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <strong>UN système centralisé. CINQ portails spécialisés. ZÉRO fragmentation.</strong><br />
            La première solution véritablement unifiée pour gérer l'alimentation scolaire au Québec.
          </p>
          <div className="hero-cta animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <button onClick={scrollToContact} className="btn-primary">
              Découvrir la plateforme
              <ArrowRight size={20} />
            </button>
            <button className="btn-secondary" onClick={() => document.getElementById('architecture')?.scrollIntoView({ behavior: 'smooth' })}>
              Voir l'architecture
            </button>
          </div>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">4</div>
              <div className="stat-label">Acteurs connectés</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">Système unifié</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Automatisé</div>
            </div>
          </div>
        </div>
      </section>

      {/* Ce que c'EST vs Ce que ce N'EST PAS */}
      <section className="section is-not-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Comprenez EcoLunch</h2>
            <p className="section-subtitle">Ce N'EST PAS une marketplace. C'EST une plateforme de gestion centralisée.</p>
          </div>

          <div className="is-not-grid">
            <div className="is-not-card not-card">
              <div className="is-not-icon"><X size={48} /></div>
              <h3>❌ Ce que ce N'EST PAS</h3>
              <ul>
                <li><X size={18} /> Un regroupement de traiteurs (style Uber Eats)</li>
                <li><X size={18} /> Une marketplace pour "choisir son traiteur"</li>
                <li><X size={18} /> Plusieurs sites web séparés</li>
                <li><X size={18} /> Un simple service de commande en ligne</li>
                <li><X size={18} /> Un système fragmenté avec intégrations tierces</li>
              </ul>
            </div>

            <div className="is-not-card is-card">
              <div className="is-not-icon"><Check size={48} /></div>
              <h3>✅ Ce que c'EST</h3>
              <ul>
                <li><Check size={18} /> <strong>UN système de gestion centralisé (ERP/SaaS)</strong></li>
                <li><Check size={18} /> <strong>CINQ portails dans UNE plateforme</strong></li>
                <li><Check size={18} /> Architecture hiérarchique à 5 niveaux</li>
                <li><Check size={18} /> Données centralisées et sécurisées</li>
                <li><Check size={18} /> Flux d'information orchestrés en temps réel</li>
                <li><Check size={18} /> Automatisation complète de bout en bout</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture System */}
      <section id="architecture" className="section architecture-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Architecture du Système</h2>
            <p className="section-subtitle">Une plateforme centralisée avec 5 portails hiérarchiques interconnectés</p>
          </div>

          <div className="architecture-diagram">
            <div className="hub-spoke-container" data-testid="hub-spoke-diagram-old">
              <div className="architecture-center">
                <div className="center-hub">
                  <img src={require('../assets/logo-fox-neon.png')} alt="EcoLunch" className="center-hub-fox" />
                  <span>EcoLunch</span>
                  <p>Back-End Central</p>
                </div>
              </div>

              <div className="hub-spoke-orbit">
                <svg className="hub-spoke-lines" viewBox="0 0 620 620">
                  <line className="hub-spoke-line" x1="310" y1="310" x2="310" y2="120" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="491" y2="257" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="424" y2="464" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="196" y2="464" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="129" y2="257" />
                </svg>
              </div>

              <div className="hub-spoke-nodes">
                <div className="actor-node parent-node spoke-pos-0">
                  <Users size={24} />
                  <h4>Parents</h4>
                  <p>Interface</p>
                </div>
                <div className="actor-node school-node spoke-pos-1">
                  <School size={24} />
                  <h4>Écoles</h4>
                  <p>Cockpit</p>
                </div>
                <div className="actor-node caterer-node spoke-pos-2">
                  <ChefHat size={24} />
                  <h4>Traiteurs</h4>
                  <p>Back-office</p>
                </div>
                <div className="actor-node direction-node spoke-pos-3">
                  <Building2 size={24} />
                  <h4>Directions</h4>
                  <p>Gouvernance</p>
                </div>
                <div className="actor-node css-node spoke-pos-4">
                  <Landmark size={24} />
                  <h4>CSS</h4>
                  <p>Politique</p>
                </div>
              </div>
            </div>
          </div>

          <div className="architecture-features">
            <div className="arch-feature">
              <div className="arch-feature-icon"><Layers size={24} /></div>
              <h4>Un seul système</h4>
              <p>Pas de sites multiples, pas de fragmentation</p>
            </div>
            <div className="arch-feature">
              <div className="arch-feature-icon"><GitBranch size={24} /></div>
              <h4>Données centralisées</h4>
              <p>Flux d'information orchestrés en temps réel</p>
            </div>
            <div className="arch-feature">
              <div className="arch-feature-icon"><Shield size={24} /></div>
              <h4>Isolation sécurisée</h4>
              <p>Chaque acteur dans sa bulle protégée</p>
            </div>
          </div>
        </div>
      </section>

      {/* Flux Opérationnel */}
      <section className="section flow-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Flux Opérationnel</h2>
            <p className="section-subtitle">Comment les 5 acteurs interagissent dans le système</p>
          </div>

          <div className="flow-steps">
            <div className="flow-step">
              <div className="flow-number">1</div>
              <div className="flow-content">
                <Users size={28} />
                <h4>Parent commande</h4>
                <p>Un parent commande pour tous ses enfants en <strong>15 secondes</strong> via l'application</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowDown size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">2</div>
              <div className="flow-content">
                <ChefHat size={28} />
                <h4>Traiteur reçoit automatiquement</h4>
                <p>La commande est <strong>automatiquement intégrée</strong> à la fiche de production du traiteur</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowDown size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">3</div>
              <div className="flow-content">
                <School size={28} />
                <h4>École voit en temps réel</h4>
                <p>L'école voit le volume ajusté dans son tableau de bord <strong>instantanément</strong></p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowDown size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">4</div>
              <div className="flow-content">
                <Building2 size={28} />
                <h4>Direction supervise</h4>
                <p>La Direction d'école constate la hausse de participation dans sa vue consolidée</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowDown size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">5</div>
              <div className="flow-content">
                <Landmark size={28} />
                <h4>CSS pilote la politique alimentaire</h4>
                <p>Le Centre de services scolaires analyse les données consolidées et ajuste les <strong>politiques alimentaires</strong> à l'échelle du territoire</p>
              </div>
            </div>
          </div>

          <div className="flow-info">
            <div className="flow-info-item">
              <ArrowUp size={20} className="flow-icon-up" />
              <div>
                <h5>Flux Verticaux Ascendants</h5>
                <p>Commandes, feedbacks, alertes remontent la hiérarchie</p>
              </div>
            </div>
            <div className="flow-info-item">
              <ArrowDown size={20} className="flow-icon-down" />
              <div>
                <h5>Flux Verticaux Descendants</h5>
                <p>Politiques, directives, normes descendent la hiérarchie</p>
              </div>
            </div>
            <div className="flow-info-item">
              <GitBranch size={20} className="flow-icon-horizontal" />
              <div>
                <h5>Flux Horizontaux</h5>
                <p>Communication directe entre acteurs pour réactivité immédiate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Acteurs avec Onglets - PARTIE 1 SEULEMENT pour l'instant */}
      <section id="acteurs" className="section actors-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Les 4 Acteurs de l'Écosystème</h2>
            <p className="section-subtitle">
              Chaque acteur dispose de son portail dédié avec fonctionnalités spécifiques
            </p>
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-navigation">
            <button 
              className={`tab-btn ${activeActorTab === 'parents' ? 'active' : ''}`}
              onClick={() => setActiveActorTab('parents')}
            >
              <Users size={20} />
              <span>Parents</span>
            </button>
            <button 
              className={`tab-btn ${activeActorTab === 'schools' ? 'active' : ''}`}
              onClick={() => setActiveActorTab('schools')}
            >
              <School size={20} />
              <span>Écoles</span>
            </button>
            <button 
              className={`tab-btn ${activeActorTab === 'caterers' ? 'active' : ''}`}
              onClick={() => setActiveActorTab('caterers')}
            >
              <ChefHat size={20} />
              <span>Traiteurs</span>
            </button>
            <button 
              className={`tab-btn ${activeActorTab === 'directions' ? 'active' : ''}`}
              onClick={() => setActiveActorTab('directions')}
            >
              <Building2 size={20} />
              <span>Directions</span>
            </button>
          </div>

          {/* Tab Content - Parents */}
          {activeActorTab === 'parents' && <ParentsTab />}

          {/* Tab Content - Schools */}
          {activeActorTab === 'schools' && <SchoolsTab />}

          {/* Tab Content - Caterers */}
          {activeActorTab === 'caterers' && <CaterersTab />}

          {/* Tab Content - Directions */}
          {activeActorTab === 'directions' && <DirectionsTab />}
        </div>
      </section>

      {/* Co-Parentalité Section */}
      <section id="coparentalite" className="section coparent-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Module Co-Parentalité</h2>
            <p className="section-subtitle">Gestion collaborative pour familles modernes avec permissions configurables</p>
          </div>

          <div className="coparent-grid">
            <div className="coparent-feature">
              <div className="coparent-icon"><UserPlus size={32} /></div>
              <h4>Accès Multi-Parents</h4>
              <ul>
                <li>Invitation via lien unique</li>
                <li>Validation double approbation</li>
                <li>Synchronisation temps réel</li>
                <li>Gestion garde partagée</li>
              </ul>
            </div>

            <div className="coparent-feature">
              <div className="coparent-icon"><Settings size={32} /></div>
              <h4>Permissions Configurables</h4>
              <ul>
                <li>Qui peut commander</li>
                <li>Qui peut consulter</li>
                <li>Qui peut payer</li>
                <li>Rôles personnalisables</li>
              </ul>
            </div>

            <div className="coparent-feature">
              <div className="coparent-icon"><Lock size={32} /></div>
              <h4>Sécurité Renforcée</h4>
              <ul>
                <li>Paiements chiffrés séparés</li>
                <li>Journal d'activité complet</li>
                <li>Traçabilité IP et horodatage</li>
                <li>Aucun partage de données bancaires</li>
              </ul>
            </div>

            <div className="coparent-feature">
              <div className="coparent-icon"><DollarSign size={32} /></div>
              <h4>Partage des Paiements</h4>
              <ul>
                <li>Split automatique (50/50, 60/40, etc.)</li>
                <li>Validation double approbation</li>
                <li>Factures individuelles</li>
                <li>Rapports PDF exportables</li>
              </ul>
            </div>
          </div>

          <div className="external-payment">
            <h3>🔗 Lien de Paiement Externe - Fonctionnalité Unique</h3>
            <p className="external-payment-intro">Parent gère la commande complète, tiers paie via interface dédiée</p>
            
            <div className="payment-steps">
              <div className="payment-step">
                <div className="step-num">1</div>
                <div>
                  <h5>Parent crée la commande</h5>
                  <p>Configure tous les repas et préférences</p>
                </div>
              </div>
              <div className="payment-step">
                <div className="step-num">2</div>
                <div>
                  <h5>Génération lien personnalisé</h5>
                  <p>Système génère un lien de paiement unique</p>
                </div>
              </div>
              <div className="payment-step">
                <div className="step-num">3</div>
                <div>
                  <h5>Envoi au payeur tiers</h5>
                  <p>Via email, SMS ou WhatsApp</p>
                </div>
              </div>
              <div className="payment-step">
                <div className="step-num">4</div>
                <div>
                  <h5>Paiement sans inscription</h5>
                  <p>Tiers paie directement, reçoit reçu instantané</p>
                </div>
              </div>
            </div>

            <div className="payment-usecases">
              <div className="usecase-card">
                <span className="usecase-emoji">👴</span>
                <h5>Grands-Parents</h5>
                <p>Contribuent aux repas de leurs petits-enfants</p>
              </div>
              <div className="usecase-card">
                <span className="usecase-emoji">🎂</span>
                <h5>Cadeau Anniversaire</h5>
                <p>Offrir des repas scolaires comme cadeau utile</p>
              </div>
              <div className="usecase-card">
                <span className="usecase-emoji">👥</span>
                <h5>Famille Élargie</h5>
                <p>Oncles, tantes, parrains qui veulent aider</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jeu Enfants Section */}
      <section id="jeu-enfants" className="section game-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Jeu CliC & CroQ™ - Portail Enfant</h2>
            <p className="section-subtitle">Gamification pour engager les enfants dans leurs choix alimentaires</p>
          </div>

          <div className="game-features">
            <div className="game-feature-card">
              <Gamepad2 size={48} className="game-icon" />
              <h4>Jeu de Commande CliC & CroQ™</h4>
              <p>Interface ludique où les enfants participent activement à la sélection de leurs repas</p>
            </div>

            <div className="game-feature-card">
              <Users size={48} className="game-icon" />
              <h4>Création d'Avatars</h4>
              <p>Personnalisation complète avec avatars uniques et thèmes colorés</p>
            </div>

            <div className="game-feature-card">
              <Award size={48} className="game-icon" />
              <h4>Système de Notation Enfant</h4>
              <p>Les enfants notent leurs repas avec smileys, contribuant à l'amélioration des menus</p>
            </div>

            <div className="game-feature-card">
              <Sparkles size={48} className="game-icon" />
              <h4>Thèmes & Récompenses</h4>
              <p>Débloquer des thèmes et badges pour encourager participation et découverte</p>
            </div>
          </div>

          <div className="game-benefits">
            <h4>💡 Avantages Pédagogiques</h4>
            <div className="benefits-list">
              <div className="benefit-item-game">
                <CheckCircle2 size={20} />
                <span>Autonomisation des enfants dans leurs choix alimentaires</span>
              </div>
              <div className="benefit-item-game">
                <CheckCircle2 size={20} />
                <span>Apprentissage ludique des bonnes habitudes nutritionnelles</span>
              </div>
              <div className="benefit-item-game">
                <CheckCircle2 size={20} />
                <span>Optimisation des coûts par meilleure prédiction des besoins</span>
              </div>
              <div className="benefit-item-game">
                <CheckCircle2 size={20} />
                <span>Feedback précieux pour traiteurs et écoles</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Messagerie Section */}
      <section id="messagerie" className="section messaging-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Messagerie Interne EcoLoop™</h2>
            <p className="section-subtitle">Communication orchestrée entre tous les acteurs - Plus puissant que WhatsApp</p>
          </div>

          <div className="messaging-features-grid">
            <div className="messaging-feature">
              <MessageSquare size={32} />
              <h4>Communication Multi-Acteurs</h4>
              <p>Parents ↔ Traiteurs ↔ Écoles ↔ Directions</p>
            </div>

            <div className="messaging-feature">
              <FileImage size={32} />
              <h4>Documents & Fichiers</h4>
              <p>Partage sécurisé d'images, PDF, documents</p>
            </div>

            <div className="messaging-feature">
              <Users size={32} />
              <h4>Groupes de Discussion</h4>
              <p>Création de groupes pour résolution collaborative</p>
            </div>

            <div className="messaging-feature">
              <Bell size={32} />
              <h4>Notifications Intelligentes</h4>
              <p>Alertes temps réel configurables par priorité</p>
            </div>

            <div className="messaging-feature">
              <Lock size={32} />
              <h4>Permissions Avancées</h4>
              <p>Contrôle granulaire qui peut parler à qui</p>
            </div>

            <div className="messaging-feature">
              <FileText size={32} />
              <h4>Historique Complet</h4>
              <p>Archivage et recherche dans toutes conversations</p>
            </div>
          </div>

          <div className="messaging-permissions">
            <h4>🔐 Système de Permissions Hiérarchique</h4>
            <div className="permissions-grid">
              <div className="permission-card">
                <h5>Direction → Tous</h5>
                <p><strong>Intervention sans permission</strong> - La Direction peut intervenir dans toute conversation sans autorisation préalable</p>
              </div>
              <div className="permission-card">
                <h5>École → Parent</h5>
                <p><strong>Autorisation requise</strong> - L'école doit autoriser le parent à initier une conversation</p>
              </div>
              <div className="permission-card">
                <h5>Traiteur → École</h5>
                <p><strong>Canal direct</strong> - Communication bidirectionnelle automatique pour opérations quotidiennes</p>
              </div>
              <div className="permission-card">
                <h5>Groupes Tripartites</h5>
                <p><strong>Création collaborative</strong> - Traiteur ou École peuvent créer un groupe avec Parent pour résolution</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sécurité Section */}
      <section id="securite" className="section security-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Sécurité & Confidentialité</h2>
            <p className="section-subtitle">Protection absolue des données avec architecture multi-tenant isolée</p>
          </div>

          <div className="security-architecture">
            <div className="security-card">
              <Shield size={48} className="security-icon" />
              <h4>Multi-Tenant Isolé</h4>
              <p>Chaque entité opère dans un espace protégé. Isolation complète des données. Aucune fuite possible entre tenants.</p>
            </div>

            <div className="security-card">
              <Lock size={48} className="security-icon" />
              <h4>Chiffrement Complet</h4>
              <p>En transit (TLS/SSL) et au repos (AES-256). Toutes les données sensibles chiffrées bout en bout.</p>
            </div>

            <div className="security-card">
              <FileText size={48} className="security-icon" />
              <h4>Audit Log Complet</h4>
              <p>Traçabilité de toutes les actions et accès. Journalisation complète avec horodatage et IP.</p>
            </div>

            <div className="security-card">
              <Database size={48} className="security-icon" />
              <h4>Sauvegardes Automatiques</h4>
              <p>Backup quotidien avec rétention configurable. Restauration rapide en cas de besoin.</p>
            </div>
          </div>

          <div className="rbac-section">
            <h4>🔐 RBAC - Role-Based Access Control</h4>
            <div className="rbac-grid">
              <div className="rbac-role">
                <Users size={24} />
                <h5>Parent</h5>
                <ul>
                  <li>Ses enfants uniquement</li>
                  <li>Ses commandes et paiements</li>
                  <li>Historique personnel</li>
                  <li>Évaluations de ses enfants</li>
                </ul>
              </div>

              <div className="rbac-role">
                <School size={24} />
                <h5>École</h5>
                <ul>
                  <li>Son établissement uniquement</li>
                  <li>Ses statistiques locales</li>
                  <li>Gestion des menus de l'école</li>
                  <li>Coordination avec son traiteur</li>
                </ul>
              </div>

              <div className="rbac-role">
                <ChefHat size={24} />
                <h5>Traiteur</h5>
                <ul>
                  <li>Ses commandes uniquement</li>
                  <li>Ses écoles clientes</li>
                  <li>Statistiques de vente</li>
                  <li>Paiements reçus</li>
                </ul>
              </div>

              <div className="rbac-role">
                <Building2 size={24} />
                <h5>Direction</h5>
                <ul>
                  <li>Son portefeuille d'écoles</li>
                  <li>Rapports consolidés</li>
                  <li>Supervision multi-écoles</li>
                  <li>Accès audit complet</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section id="certification" className="section certification-section">
        <div className="section-container">
          <div className="section-header">
            <div className="cert-badge-large">
              <Award size={64} />
              <span>CERTIFICATION</span>
              <h2>FutÉcoLuncH</h2>
            </div>
            <p className="section-subtitle">
              Normes de qualité et confiance - Seuls les traiteurs certifiés peuvent rejoindre l'écosystème
            </p>
          </div>

          <div className="certification-content">
            <div className="cert-intro">
              <p>
                La Certification EcoLunch garantit que seuls des traiteurs professionnels, fiables et engagés dans des pratiques durables peuvent rejoindre notre écosystème. Un processus rigoureux pour protéger tous les acteurs.
              </p>
            </div>

            <div className="cert-criteria">
              <div className="cert-criterion">
                <div className="cert-icon"><Shield size={32} /></div>
                <h4>Licence et Conformité</h4>
                <p>Licences alimentaires valides et conformité réglementaire complète avec les normes Québécoises</p>
              </div>

              <div className="cert-criterion">
                <div className="cert-icon"><Globe size={32} /></div>
                <h4>Pratiques Durables</h4>
                <p>Engagement envers des opérations écoresponsables et durables. Optimisation intelligente des ressources.</p>
              </div>

              <div className="cert-criterion">
                <div className="cert-icon"><FileText size={32} /></div>
                <h4>Sécurité Alimentaire</h4>
                <p>Certification HACCP et protocoles de sécurité alimentaire rigoureux. Formation continue obligatoire.</p>
              </div>

              <div className="cert-criterion">
                <div className="cert-icon"><Award size={32} /></div>
                <h4>Expérience Prouvée</h4>
                <p>Expérience démontrée en restauration scolaire et programmes alimentaires. Références vérifiables.</p>
              </div>

              <div className="cert-criterion">
                <div className="cert-icon"><Users size={32} /></div>
                <h4>Capacité Opérationnelle</h4>
                <p>Capacité de production suffisante, équipement adéquat, équipe qualifiée et gestion professionnelle.</p>
              </div>

              <div className="cert-criterion">
                <div className="cert-icon"><CheckCircle2 size={32} /></div>
                <h4>Engagement Qualité</h4>
                <p>Standards nutritionnels élevés, ingrédients de qualité, menu varié et adapté aux besoins des enfants.</p>
              </div>
            </div>

            <div className="cert-benefits">
              <h4>💎 Avantages de la Certification</h4>
              <div className="cert-benefits-grid">
                <div className="cert-benefit">
                  <Sparkles size={24} />
                  <span>Crédibilité instantanée auprès des écoles</span>
                </div>
                <div className="cert-benefit">
                  <TrendingUp size={24} />
                  <span>Accès à un réseau d'écoles qualifiées</span>
                </div>
                <div className="cert-benefit">
                  <Award size={24} />
                  <span>Badge de confiance sur la plateforme</span>
                </div>
                <div className="cert-benefit">
                  <Users size={24} />
                  <span>Support marketing et visibilité EcoLunch</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EcoLunch Connect Section */}
      <section id="connect" className="section connect-section">
        <div className="section-container">
          <div className="section-header">
            <div className="connect-badge">🤝 Service Clé en Main</div>
            <h2 className="section-title">EcoLunch Connect</h2>
            <p className="section-subtitle">
              De zéro au lancement - Entièrement géré par EcoLunch
            </p>
          </div>

          <div className="connect-intro">
            <p>
              Un service clé en main entièrement géré qui met en lien les écoles sans service de repas avec des traiteurs certifiés EcoLunch. 
              EcoLunch pilote tout le processus—de la certification des traiteurs et du jumelage, à l'implantation complète, la formation et la supervision du lancement—pendant que l'école choisit simplement parmi les fournisseurs pré-qualifiés.
            </p>
          </div>

          <div className="connect-steps-detailed">
            <div className="connect-step-card">
              <div className="step-number-large">1</div>
              <div className="step-icon"><Shield size={40} /></div>
              <h4>Certification Traiteurs</h4>
              <p>Processus rigoureux de validation et certification EcoLunch pour garantir la qualité</p>
              <ul>
                <li>Vérification licences et conformité</li>
                <li>Audit des installations</li>
                <li>Évaluation capacité opérationnelle</li>
                <li>Validation standards qualité</li>
              </ul>
            </div>

            <div className="connect-step-card">
              <div className="step-number-large">2</div>
              <div className="step-icon"><Users size={40} /></div>
              <h4>Jumelage École-Traiteur</h4>
              <p>Mise en relation intelligente basée sur les besoins, la localisation et les capacités</p>
              <ul>
                <li>Analyse des besoins de l'école</li>
                <li>Matching avec traiteurs disponibles</li>
                <li>Présentation des options qualifiées</li>
                <li>Facilitation de la sélection</li>
              </ul>
            </div>

            <div className="connect-step-card">
              <div className="step-number-large">3</div>
              <div className="step-icon"><Settings size={40} /></div>
              <h4>Implantation Complète</h4>
              <p>Configuration technique et opérationnelle complète gérée par EcoLunch</p>
              <ul>
                <li>Setup de la plateforme</li>
                <li>Configuration des comptes</li>
                <li>Intégration des menus</li>
                <li>Tests et validation</li>
              </ul>
            </div>

            <div className="connect-step-card">
              <div className="step-number-large">4</div>
              <div className="step-icon"><Award size={40} /></div>
              <h4>Formation & Lancement</h4>
              <p>Formation complète et supervision jusqu'à l'opération autonome</p>
              <ul>
                <li>Formation équipes école et traiteur</li>
                <li>Communication aux parents</li>
                <li>Supervision des premières semaines</li>
                <li>Support continu post-lancement</li>
              </ul>
            </div>
          </div>

          <div className="connect-dual-benefits">
            <div className="connect-benefit-box schools">
              <School size={48} />
              <h4>Pour les Écoles</h4>
              <ul>
                <li><CheckCircle2 size={18} /> Aucune connaissance technique requise</li>
                <li><CheckCircle2 size={18} /> Traiteurs pré-qualifiés seulement</li>
                <li><CheckCircle2 size={18} /> Support complet EcoLunch</li>
                <li><CheckCircle2 size={18} /> Déploiement rapide et sans stress</li>
                <li><CheckCircle2 size={18} /> Accompagnement à chaque étape</li>
              </ul>
            </div>

            <div className="connect-benefit-box caterers">
              <ChefHat size={48} />
              <h4>Pour les Traiteurs</h4>
              <ul>
                <li><CheckCircle2 size={18} /> Crédibilité instantanée</li>
                <li><CheckCircle2 size={18} /> Accès à nouveaux contrats écoles</li>
                <li><CheckCircle2 size={18} /> Marketing et visibilité EcoLunch</li>
                <li><CheckCircle2 size={18} /> Opportunités de croissance</li>
                <li><CheckCircle2 size={18} /> Formation et support technique</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Concurrentiels Section */}
      <section id="avantages" className="section competitive-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Avantages Concurrentiels</h2>
            <p className="section-subtitle">
              Pourquoi EcoLunch surpasse toutes les autres solutions
            </p>
          </div>

          <div className="competitive-grid">
            <div className="competitive-card">
              <div className="competitive-icon"><Zap size={48} /></div>
              <h4>Innovation Technologique</h4>
              <p>IA propriétaire développée exclusivement pour la gestion de traiteur alimentaire. Algorithmes entraînés sur des années de données secteur.</p>
              <div className="competitive-highlight">🚀 Aucune IA générique - 100% adaptée</div>
            </div>

            <div className="competitive-card">
              <div className="competitive-icon"><Globe size={48} /></div>
              <h4>Approche Unifiée Unique</h4>
              <p>SEULE plateforme au Québec qui centralise Parents, Écoles, Traiteurs ET Directions dans UN système cohérent.</p>
              <div className="competitive-highlight">⚡ Du jamais vu au Québec</div>
            </div>

            <div className="competitive-card">
              <div className="competitive-icon"><TrendingUp size={48} /></div>
              <h4>ROI Immédiat</h4>
              <p>Économie de temps massive (automatisation quasi-complète), optimisation intelligente des coûts d'approvisionnement, réduction des coûts d'opération.</p>
              <div className="competitive-highlight">💰 Résultats garantis</div>
            </div>

            <div className="competitive-card">
              <div className="competitive-icon"><Shield size={48} /></div>
              <h4>Sécurité de Classe Entreprise</h4>
              <p>Architecture multi-tenant isolée, chiffrement AES-256, audit complet, conformité totale. Serveurs 100% au Québec.</p>
              <div className="competitive-highlight">🔒 Données protégées</div>
            </div>

            <div className="competitive-card">
              <div className="competitive-icon"><Users size={48} /></div>
              <h4>Expérience Utilisateur Supérieure</h4>
              <p>Interface intuitive pour tous les acteurs. Commande parent en 15 secondes. Zéro courbe d'apprentissage.</p>
              <div className="competitive-highlight">✨ Design primé</div>
            </div>

            <div className="competitive-card">
              <div className="competitive-icon"><Target size={48} /></div>
              <h4>Support & Accompagnement</h4>
              <p>Support VIP 24/7, accompagnement stratégique, formation complète, implantation clé en main.</p>
              <div className="competitive-highlight">🤝 Jamais seuls</div>
            </div>
          </div>

          <div className="roi-section">
            <h3>💎 Bénéfices Mesurables</h3>
            <div className="roi-grid">
              <div className="roi-item">
                <div className="roi-number">90%</div>
                <div className="roi-label">Réduction temps administratif</div>
              </div>
              <div className="roi-item">
                <div className="roi-number">100%</div>
                <div className="roi-label">Précision des commandes</div>
              </div>
              <div className="roi-item">
                <div className="roi-number">15 sec</div>
                <div className="roi-label">Temps de commande parent</div>
              </div>
              <div className="roi-item">
                <div className="roi-number">100%</div>
                <div className="roi-label">Traçabilité garantie</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Roadmap Section */}
      <section id="vision" className="section vision-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Vision à Long Terme</h2>
            <p className="section-subtitle">
              4 phases pour transformer l'alimentation scolaire au Québec et au-delà
            </p>
          </div>

          <div className="roadmap">
            <div className="roadmap-phase">
              <div className="phase-number">Phase 1</div>
              <div className="phase-content">
                <h4>🎯 Adoption Régionale (2025-2026)</h4>
                <p>Déploiement dans les premières régions pilotes du Québec. Validation du modèle avec écoles partenaires.</p>
                <ul>
                  <li>10-20 écoles pilotes</li>
                  <li>5-10 traiteurs certifiés</li>
                  <li>Validation du modèle opérationnel</li>
                  <li>Optimisation continue basée sur feedback</li>
                </ul>
              </div>
            </div>

            <div className="roadmap-phase">
              <div className="phase-number">Phase 2</div>
              <div className="phase-content">
                <h4>🚀 Expansion Québec (2026-2027)</h4>
                <p>Déploiement à travers toutes les régions du Québec. Partenariats avec commissions scolaires.</p>
                <ul>
                  <li>100+ écoles actives</li>
                  <li>Réseau provincial de traiteurs</li>
                  <li>Intégration Directions d'école</li>
                  <li>Modules IA avancés (QPlanIQ™, LogistIQ™)</li>
                </ul>
              </div>
            </div>

            <div className="roadmap-phase">
              <div className="phase-number">Phase 3</div>
              <div className="phase-content">
                <h4>🇨🇦 Expansion Canada (2027-2028)</h4>
                <p>Déploiement dans les autres provinces canadiennes. Adaptation aux réglementations locales.</p>
                <ul>
                  <li>Ontario, Colombie-Britannique, Alberta</li>
                  <li>500+ écoles à travers le Canada</li>
                  <li>Réseau national de traiteurs certifiés</li>
                  <li>Partenariats gouvernementaux</li>
                </ul>
              </div>
            </div>

            <div className="roadmap-phase">
              <div className="phase-number">Phase 4</div>
              <div className="phase-content">
                <h4>🌍 Innovation Continue (2028+)</h4>
                <p>R&D continue, nouvelles fonctionnalités, expansion internationale potentielle.</p>
                <ul>
                  <li>Nouvelles fonctionnalités IA</li>
                  <li>Expansion secteur privé (entreprises, hôpitaux)</li>
                  <li>Recherche & développement nutrition</li>
                  <li>Impact environnemental mesurable</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section faq-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Questions Fréquentes</h2>
            <p className="section-subtitle">
              Tout ce que vous devez savoir pour démarrer
            </p>
          </div>

          <div className="faq-grid">
            <div className="faq-category">
              <h3>Pour les Écoles</h3>
              <div className="faq-item">
                <h5>Combien de temps prend l'implantation?</h5>
                <p>Avec EcoLunch Connect, de 2 à 4 semaines de la décision au premier repas servi. Nous gérons tout.</p>
              </div>
              <div className="faq-item">
                <h5>Quel est le coût pour l'école?</h5>
                <p>EcoLunch est gratuit pour les écoles. Les parents paient leurs repas et les frais d'abonnement optionnels.</p>
              </div>
              <div className="faq-item">
                <h5>Qui gère la plateforme au quotidien?</h5>
                <p>Minimum de gestion requise. Tout est automatisé. L'école supervise simplement via le tableau de bord.</p>
              </div>
            </div>

            <div className="faq-category">
              <h3>Pour les Traiteurs</h3>
              <div className="faq-item">
                <h5>Comment obtenir la certification?</h5>
                <p>Processus en 3 étapes : Dossier → Audit → Certification. Durée moyenne 2-3 semaines.</p>
              </div>
              <div className="faq-item">
                <h5>Quels sont les frais?</h5>
                <p>Plans Essentiel (250$/mois) ou Plus (500$/mois). Aucun frais caché. Support inclus.</p>
              </div>
              <div className="faq-item">
                <h5>Puis-je gérer plusieurs écoles?</h5>
                <p>Absolument. EcoLunch est conçu pour le multi-écoles avec gestion centralisée.</p>
              </div>
            </div>

            <div className="faq-category">
              <h3>Pour les Parents</h3>
              <div className="faq-item">
                <h5>Comment s'inscrire?</h5>
                <p>Inscription en 5 secondes via l'app mobile ou web. Un compte pour tous vos enfants.</p>
              </div>
              <div className="faq-item">
                <h5>Quels sont les coûts?</h5>
                <p>Plan Gratuit disponible. Plans Plus (2$/mois) et Signature (2.99$/mois) pour fonctionnalités avancées.</p>
              </div>
              <div className="faq-item">
                <h5>Mes données sont-elles sécurisées?</h5>
                <p>Chiffrement AES-256, serveurs au Québec, conformité totale. Vos données sont protégées.</p>
              </div>
            </div>
          </div>

          <div className="faq-cta">
            <h4>Vous avez d'autres questions?</h4>
            <p>Notre équipe est disponible pour répondre à toutes vos interrogations</p>
            <button className="btn-primary" onClick={scrollToContact}>
              Contactez-nous
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
              <div className="footer-badge-100">
                <span>🍁</span>
                <span>100% Québécois</span>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Plateforme</h4>
                <a href="#architecture">Architecture</a>
                <a href="#acteurs">Acteurs</a>
              </div>

              <div className="footer-column">
                <h4>Solutions</h4>
                <a href="#acteurs">Pour les Parents</a>
                <a href="#acteurs">Pour les Écoles</a>
                <a href="#acteurs">Pour les Traiteurs</a>
                <a href="#acteurs">Pour les Directions</a>
              </div>

              <div className="footer-column">
                <h4>Entreprise</h4>
                <a href="#">À propos</a>
                <a href="#contact-section">Contact</a>
                <a href="#">Carrières</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
              <div className="footer-legal-links">
                <a href="#">Confidentialité</a>
                <a href="#">Conditions</a>
                <a href="#">Sécurité</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;